import { ClipboardIcon } from "@heroicons/react/24/outline";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useLocation, useSearchParams } from "react-router-dom";

// Images
import profileImg from "../../../assets/images/anushka-profile.svg";
import webCam from "../../../assets/images/video.svg";
import { authenticatedFetch } from "../../../services/apiInterviewService";
import profileImgMale1 from "../../../assets/images/profiles/male5.png";
import profileImgMale2 from "../../../assets/images/profiles/male2.png";
import profileImgMale3 from "../../../assets/images/profiles/male3.png";
import profileImgFemale1 from "../../../assets/images/profiles/female1.png";
import profileImgFemale2 from "../../../assets/images/profiles/female3.png";
import profileImgFemale3 from "../../../assets/images/profiles/female4.png";
import videoUnchecked from "../../../assets/images/video-unchecked.svg";
import videochecked from "../../../assets/images/video-checked.svg";
import speakerUnchecked from "../../../assets/images/speaker-unchecked.svg";
import speakerChecked from "../../../assets/images/speaker-checked.svg";
import microphoneUnchecked from "../../../assets/images/microphone-unchecked.svg";
import microphoneChecked from "../../../assets/images/microphone-checked.svg";
import greenTickMark from "../../../assets/images/tick-mark-green.svg";
import useNetworkStability from "../../../services/useNetworkStability";
import useHeadphoneMonitor from "../../../hooks/useHeadphoneMonitor";
import { useMultiMonitorDetection } from "../../../hooks/useMultiMonitorDetection";
import { listenDesktopAgent } from "../../../services/desktopAgentService";
import { listenSession } from "../../../services/firestoreService";
import yesIcon from "../../../assets/images/yes-icon.svg";
import noIcon from "../../../assets/images/no-icon.svg";
import audioWave from "../../../assets/images/audio-wave-icon-green.gif";

export function MeetingDetailsScreen({
  onClickJoin,
  _handleOnCreateMeeting,
  participantName,
  setParticipantName,
  onClickStartMeeting,
}) {
  const location = useLocation();
  const [params] = useSearchParams();

  const redirectFrom =
    location.state?.redirectFrom || params.get("redirectFrom") || "allSet";

  const buttonText =
    redirectFrom === "restored" ? "Continue Assessment" : "Start Assessment";
  const isStartAssessment = buttonText === "Start Assessment";
  const [apiAlive, setApiAlive] = useState(false);
  const [warmingUp, setWarmingUp] = useState(false);
  const [prepSecondsLeft, setPrepSecondsLeft] = useState(null);
  const [warmupFailed, setWarmupFailed] = useState(false);
  const [meetingId, setMeetingId] = useState("");
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [panelDetails, setPanelDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDesktopAgentRunning, setIsDesktopAgentRunning] = useState(false);
  const [showBatteryWarning, setShowBatteryWarning] = useState(false);
  const batteryLowRef = useRef(false);
  const batteryUnsubscribeRef = useRef(null);

  const companyId = sessionStorage.getItem("companyId");
  const interviewId = sessionStorage.getItem("interviewId");

  const interviewAlreadyCompletedRef = useRef(
    sessionStorage.getItem("interviewAlreadyCompleted") === "true",
  );
  const [isCameraPermissionGranted, setIsCameraPermissionGranted] =
    useState(true); // optimistic default
  const [isWindowMgmtPermissionGranted, setIsWindowMgmtPermissionGranted] =
    useState(true); // optimistic
  const isHeadphoneRequired =
    sessionStorage.getItem("headphoneRequirement") === "true";

  const {
    isHeadphoneConnected,
    needsManualConfirmation: headphoneNeedsManualConfirmation,
    confirmManually: confirmHeadphoneManually,
  } = useHeadphoneMonitor({
    enabled: isHeadphoneRequired,
  });
  const isPanelDetailsValid = (data) => {
    if (!data) return false;

    // Must have panel type
    if (!data.panel) return false;

    // At least one panel member should exist
    const hasMember = data.pnlf1 || data.pnlf2 || data.pnlm1 || data.pnlm2;

    return !!hasMember;
  };

  const isPanelReady = isPanelDetailsValid(panelDetails);
  const isMobileProctoringEnabled =
    sessionStorage.getItem("mobileProctoringScreenshot") === "true";

  useEffect(() => {
    if (!companyId || !interviewId || !isMobileProctoringEnabled) return;

    const documentId = `${companyId}_${interviewId}`;

    const unsubscribe = listenSession(documentId, (data) => {
      const isCharging =
        data?.is_charging === true || data?.is_charging === "true";

      if (isCharging) {
        batteryLowRef.current = false;
        setShowBatteryWarning(false);
        return;
      }

      if (
        data?.battery_percentage === null ||
        data?.battery_percentage === ""
      ) {
        return;
      }

      const batteryPercentage = Number(data?.battery_percentage);
      if (!Number.isFinite(batteryPercentage)) return;

      if (batteryPercentage <= 10) {
        if (!batteryLowRef.current) {
          batteryLowRef.current = true;
          setShowBatteryWarning(true);
        }
        return;
      }

      batteryLowRef.current = false;
      setShowBatteryWarning(false);
    });
    batteryUnsubscribeRef.current = unsubscribe;

    return () => {
      unsubscribe?.();
      batteryUnsubscribeRef.current = null;
      batteryLowRef.current = false;
    };
  }, [companyId, interviewId]);

  const BatteryWarning = () =>
    showBatteryWarning && isMobileProctoringEnabled ? (
      <div
        className="relative z-[99] w-full flex justify-center pointer-events-none"
        role="status"
        aria-live="polite"
        style={{
          marginBottom: "10px",
          transform: "translateY(-90px)",
        }}
      >
        <div
          className="w-[min(600px,calc(100vw-2rem))] rounded-xl p-4 backdrop-blur-sm"
          style={{
            border: "1px solid #fdba74",
            borderLeft: "4px solid #f97316",
            backgroundColor: "rgba(255, 239, 220, 1)",
            boxShadow: "0 8px 24px rgba(154, 52, 18, 0.18)",
            boxSizing: "border-box",
          }}
        >
          <div
            className="flex items-center justify-center"
            style={{ alignItems: "center", justifyContent: "center" }}
          >
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-lg"
              style={{
                backgroundColor: "#ffedd5",
                color: "#ea580c",
                boxShadow: "inset 0 0 0 1px #fed7aa",
              }}
            >
              🪫
            </div>
            <div className="ml-3">
              <p
                className="text-[18px] font-semibold tracking-wide"
                style={{ color: "#9a3412" }}
              >
                Low Battery
              </p>
              <p
                className="mt-1 text-[16px] leading-5"
                style={{ color: "#c2410c" }}
              >
                Your phone battery is critically low (below 10%). Please
                recharge it as soon as possible. The NeedleEye app must remain
                running throughout the assessment.
              </p>
            </div>
          </div>
        </div>
      </div>
    ) : null;

  const getNetworkMessage = (netStatus, latencyMs, lossPct) => {
    if (netStatus === "offline") {
      return {
        title: "No internet connection",
        desc: "Please reconnect to Wi-Fi or mobile data to continue.",
      };
    }

    if (lossPct >= 60) {
      return {
        title: "Internet keeps disconnecting",
        desc: "Please switch to a stable network to continue the assessment.",
      };
    }

    if (lossPct >= 30) {
      return {
        title: "Connection is very unstable",
        desc: "Your internet is dropping frequently. Please switch networks.",
      };
    }

    if (lossPct >= 15 || latencyMs > 2500) {
      return {
        title: "Connection is weak",
        desc: "You may face delays or interruptions. Move closer to Wi-Fi or switch networks.",
      };
    }

    if (lossPct >= 5 || latencyMs > 1200) {
      return {
        title: "Connection is slightly unstable",
        desc: "You may notice small delays. A stable connection is recommended.",
      };
    }

    return {
      title: "Connection looks good",
      desc: "",
    };
  };

  const { netStatus, latencyMs, lossPct } = useNetworkStability({
    pingUrl: `${window.location.origin}/ping.txt`,
    intervalMs: 6000,
    timeoutMs: 7000,
  });
  useEffect(() => {
    const isDesktopAgentRequired =
      sessionStorage.getItem("desktopAgent") === "true";

    if (!isDesktopAgentRequired) {
      setIsDesktopAgentRunning(true); // allow start button
      return;
    }

    const companyId = sessionStorage.getItem("companyId");
    const interviewId = sessionStorage.getItem("interviewId");

    if (!companyId || !interviewId) return;

    const documentId = `${companyId}_${interviewId}`;

    const unsubscribe = listenDesktopAgent(documentId, (data) => {
      if (interviewAlreadyCompletedRef.current) return;

      const isRunning =
        data?.desktop_app_status === true ||
        data?.desktop_app_status === "true";

      setIsDesktopAgentRunning(isRunning);
    });

    return () => unsubscribe && unsubscribe();
  }, []);
  // Camera permission check + live monitor
  useEffect(() => {
    let permStatus = null;

    const checkCameraPermission = async () => {
      try {
        if (navigator.permissions) {
          permStatus = await navigator.permissions.query({ name: "camera" });

          // Set initial state
          setIsCameraPermissionGranted(permStatus.state === "granted");

          // If "prompt" — try getUserMedia to resolve the real state
          if (permStatus.state === "prompt") {
            try {
              const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
              });
              stream.getTracks().forEach((t) => t.stop());
              setIsCameraPermissionGranted(true);
            } catch {
              setIsCameraPermissionGranted(false);
            }
          }

          // Listen for runtime changes (user toggles in browser settings)
          permStatus.onchange = () => {
            if (permStatus.state === "granted") {
              // Camera re-enabled — reload so the video preview re-initialises
              window.location.reload();
            } else {
              setIsCameraPermissionGranted(false);
            }
          };
        } else {
          // Fallback: try getUserMedia directly
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: true,
            });
            stream.getTracks().forEach((t) => t.stop());
            setIsCameraPermissionGranted(true);
          } catch {
            setIsCameraPermissionGranted(false);
          }
        }
      } catch {
        setIsCameraPermissionGranted(false);
      }
    };

    checkCameraPermission();

    return () => {
      if (permStatus) permStatus.onchange = null;
    };
  }, []);
  // Window Management permission check + live monitor
  useEffect(() => {
    let permStatus = null;

    const checkWindowMgmtPermission = async () => {
      try {
        if (navigator.permissions) {
          permStatus = await navigator.permissions.query({
            name: "window-management",
          });

          setIsWindowMgmtPermissionGranted(permStatus.state === "granted");

          if (permStatus.state === "prompt") {
            try {
              await window.getScreenDetails();
              setIsWindowMgmtPermissionGranted(true);
            } catch {
              setIsWindowMgmtPermissionGranted(false);
            }
          }

          permStatus.onchange = () => {
            setIsWindowMgmtPermissionGranted(permStatus.state === "granted");
          };
        }
      } catch {
        // Browser doesn't support window-management — treat as granted (non-blocking)
        setIsWindowMgmtPermissionGranted(true);
      }
    };

    checkWindowMgmtPermission();

    return () => {
      if (permStatus) permStatus.onchange = null;
    };
  }, []);
  const isDesktopAgentRequired =
    sessionStorage.getItem("desktopAgent") === "true";
  const DesktopAgentWarningMessage = () => (
    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 max-w-2xl mx-auto">
      <div className="flex items-start">
        <div className="flex-shrink-0">⚠️</div>
        <div className="ml-3">
          <p className="text-sm font-medium text-red-800">
            Desktop Agent Required
          </p>
          <p className="mt-1 text-sm text-red-700">
            Please run NeedleEye desktop agent before starting your assessment.
          </p>
        </div>
      </div>
    </div>
  );
  const CameraWarningMessage = () => (
    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 max-w-2xl mx-auto">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-red-500 mt-0.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm12.553 1.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-red-800">
            Camera Permission Required
          </p>
          <p className="mt-1 text-sm text-red-700">
            Please enable camera access in your browser settings to start the
            assessment.
          </p>
        </div>
      </div>
    </div>
  );
  const WindowManagementWarningMessage = () => (
    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 max-w-2xl mx-auto">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-red-500 mt-0.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-red-800">
            Window Management Permission Required
          </p>
          <p className="mt-1 text-sm text-red-700">
            Please enable <strong>Window Management</strong> in your browser
            site settings to start the assessment. Click the lock icon in the
            address bar → Window Management → Allow.
          </p>
        </div>
      </div>
    </div>
  );
  const MonitorWarningMessage = () => (
    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 max-w-2xl mx-auto">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-red-500 mt-0.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium text-red-800">
            External Monitor Detected
          </p>
          <p className="mt-1 text-sm text-red-700">
            You will not be able to start the assessment until you disconnect
            the extended monitor. Always make sure to have a single screen.
          </p>
        </div>
      </div>
    </div>
  );
  const HeadphoneWarningMessage = () => (
    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 max-w-2xl mx-auto">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-red-500 mt-0.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        <div className="ml-3">
          <p className="text-sm font-medium text-red-800">
            Headphones Required
          </p>
          <p className="mt-1 text-sm text-red-700">
            You will not be able to start the assessment until you connect
            headphones.
          </p>
        </div>
      </div>
    </div>
  );

  // Shown when we can't automatically tell whether headphones are connected
  // (e.g. Windows reporting a generic "Speakers" output label even though
  // headphones are physically plugged in). Rather than blocking the
  // candidate on an unreliable guess, we ask them to confirm directly.
  const HeadphoneManualConfirmMessage = () => (
    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 max-w-2xl mx-auto">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-yellow-500 mt-0.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.598c.75 1.334-.213 2.999-1.742 2.999H3.48c-1.53 0-2.492-1.665-1.743-2.999L8.257 3.1zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        <div className="ml-3 flex-1">
          <p className="text-sm font-medium text-yellow-800">
            Can't automatically confirm headphones
          </p>
          <p className="mt-1 text-sm text-yellow-700">
            We couldn't reliably detect your headphones automatically. If you're
            currently wearing them, please confirm below.
          </p>
          <button
            type="button"
            onClick={confirmHeadphoneManually}
            className="mt-3 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium px-4 py-1.5 rounded-md"
          >
            Yes, I'm wearing headphones
          </button>
        </div>
      </div>
    </div>
  );

  const isNetworkGoodEnough = (netStatus, latencyMs, lossPct) => {
    if (netStatus === "offline" || netStatus === "checking") return false;

    const MAX_LATENCY = 7000; // ms
    const MAX_LOSS = 35; // %

    const lat = typeof latencyMs === "number" ? latencyMs : null;
    const loss = typeof lossPct === "number" ? lossPct : null;

    if (lat === null || loss === null) return false;

    return lat <= MAX_LATENCY && loss <= MAX_LOSS;
  };

  const allowProceedTop = isNetworkGoodEnough(netStatus, latencyMs, lossPct);
  const msg = getNetworkMessage(netStatus, latencyMs, lossPct);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const warmupUrl = async (url, maxAttempts = 6, timeoutMs = 15000) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const res = await fetch(url, {
          method: "GET",
          signal: controller.signal,
          cache: "no-store",
        });

        clearTimeout(timeoutId);

        if (res.ok) return true;
      } catch (err) {
        // ignore and retry
      }

      await delay(attempt * 1000);
    }
    return false;
  };

  const checkBothOnce = async () => {
    try {
      const controller1 = new AbortController();
      const t1 = setTimeout(() => controller1.abort(), 10000);

      const controller2 = new AbortController();
      const t2 = setTimeout(() => controller2.abort(), 10000);

      const server1Url = `${process.env.REACT_APP_API_BASE_URL_WITHOUTAPI}/health-check/`;
      const server2Url = `${process.env.REACT_APP_API_ROOT_URL}/health`;

      const [r1, r2] = await Promise.allSettled([
        fetch(server1Url, {
          method: "GET",
          signal: controller1.signal,
          cache: "no-store",
        }),
        fetch(server2Url, {
          method: "GET",
          signal: controller2.signal,
          cache: "no-store",
        }),
      ]);

      clearTimeout(t1);
      clearTimeout(t2);

      const ok1 = r1.status === "fulfilled" && r1.value.ok;
      const ok2 = r2.status === "fulfilled" && r2.value.ok;

      return ok1 && ok2;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const doWarmup = async () => {
      // const allowProceed = isNetworkGoodEnough(netStatus, latencyMs, lossPct);
      // if (!allowProceed) {
      //   setApiAlive(false);
      //   setWarmingUp(false);
      //   return;
      // }

      setWarmingUp(true);
      setApiAlive(false);
      setWarmupFailed(false);
      setPrepSecondsLeft(null);

      const server1Url = `${process.env.REACT_APP_API_BASE_URL_WITHOUTAPI}/health-check/`;
      const server2Url = `${process.env.REACT_APP_API_ROOT_URL}/health`;

      const [server1Alive, server2Alive] = await Promise.all([
        warmupUrl(server1Url, 6, 15000),
        warmupUrl(server2Url, 6, 15000),
      ]);

      if (!isMounted) return;

      if (server1Alive && server2Alive) {
        setApiAlive(true);
        setWarmingUp(false);
        return;
      }

      // countdown mode
      setPrepSecondsLeft(120);

      const endAt = Date.now() + 120 * 1000;
      let healthyFound = false;

      const countdownInterval = setInterval(() => {
        const remainingMs = endAt - Date.now();
        const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
        if (!isMounted) return;

        setPrepSecondsLeft(remainingSec);
        if (remainingSec <= 0) clearInterval(countdownInterval);
      }, 1000);

      const pollInterval = setInterval(async () => {
        if (Date.now() > endAt || healthyFound) return;

        const healthy = await checkBothOnce();
        if (!isMounted) return;

        if (healthy) {
          healthyFound = true;
          setApiAlive(true);
          setWarmingUp(false);
          setPrepSecondsLeft(null);

          clearInterval(pollInterval);
          clearInterval(countdownInterval);
        }
      }, 6000);

      setTimeout(() => {
        if (!isMounted) return;
        if (!healthyFound) setWarmupFailed(true);

        clearInterval(pollInterval);
        clearInterval(countdownInterval);
      }, 120 * 1000);
    };

    doWarmup();

    return () => {
      isMounted = false;
    };
  }, []);

  //  NEW: Monitor detection hook
  const [hasExternalMonitor, setHasExternalMonitor] = useState(false);
  const { hasMultipleMonitors, monitorCount } = useMultiMonitorDetection({
    onMonitorChange: ({ method, count, connected }) => {
      setHasExternalMonitor(connected);

      if (connected) {
        // toast.error(
        //   "External monitor detected! Please disconnect to start the assessment.",
        //   {
        //     position: "top-center",
        //     autoClose: 5000,
        //     style: {
        //       background: "#fee",
        //       color: "#dc2626",
        //       border: "1px solid #dc2626",
        //       fontWeight: "600",
        //     },
        //   },
        // );
      } else {
        // toast.success(
        //   "Single monitor detected. You can now continue the assessment.",
        //   {
        //     position: "top-center",
        //     autoClose: 3000,
        //     style: {
        //       background: "#f0fdf4",
        //       color: "#16a34a",
        //       border: "1px solid #16a34a",
        //       fontWeight: "600",
        //     },
        //   },
        // );
      }
    },
    pollingInterval: 3000,
    enableLogging: true,
  });

  // Helper to format names
  function toTitleCase(str = "") {
    return str?.toLowerCase().replace(/\b\w/g, (char) => char?.toUpperCase());
  }

  // Auto-generate meeting ID
  const generateMeetingId = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 12; i++) {
      if (i === 4 || i === 8) result += "-";
      else
        result += characters.charAt(
          Math.floor(Math.random() * characters.length),
        );
    }
    return result;
  };

  // Hardcoded username for testing
  useEffect(() => {
    const candidateName = sessionStorage.getItem("candidateName");
    if (candidateName) {
      setParticipantName(candidateName);
    } else {
      setParticipantName("Utkarsh");
    }
  }, [setParticipantName]);

  // Stop infinite Loading... when internet is OFF / unstable
  useEffect(() => {
    if (!allowProceedTop && netStatus !== "checking") {
      setIsLoading(false);
    }
  }, [allowProceedTop, netStatus]);
  useEffect(() => {
    if (sessionStorage.getItem("whiteboardReconnectPending") === null) {
      sessionStorage.setItem("whiteboardReconnectPending", "false");
    }
    if (sessionStorage.getItem("codingReconnectPending") === null) {
      sessionStorage.setItem("codingReconnectPending", "false");
    }
    if (sessionStorage.getItem("preReconnectionCheck") === null) {
      sessionStorage.setItem("preReconnectionCheck", JSON.stringify(null));
    }
  }, []);
  const runPreReconnectionCheck = async () => {
    try {
      const companyId = sessionStorage.getItem("companyId") || "";
      const interviewId = sessionStorage.getItem("interviewId");
      const url = `${process.env.REACT_APP_API_OCR_BASE_URL}/pre-reconnection-check/?interview_id=${interviewId}&company_id=${companyId}`;
      const response = await authenticatedFetch(url, { method: "POST" });
      const data = await response.json();

      sessionStorage.setItem("preReconnectionCheck", JSON.stringify(data));
      sessionStorage.setItem(
        "whiteboardReconnectPending",
        data?.is_whiteboarding_completed === false ? "true" : "false",
      );
      sessionStorage.setItem(
        "codingReconnectPending",
        data?.is_coding_completed === false ? "true" : "false",
      );

      const isInterviewCompleted =
        data?.is_interview_completed === true ||
        data?.is_interview_completed === "true";

      if (isInterviewCompleted) {
        sessionStorage.setItem("interviewAlreadyCompleted", "true");
        interviewAlreadyCompletedRef.current = true;
        setIsDesktopAgentRunning(true);
      }
    } catch (error) {
      // Non-fatal: if this check fails, fall back to the normal join flow
      // rather than blocking the candidate from getting into the meeting.
      console.error("Error fetching pre-reconnection check:", error);
      sessionStorage.setItem("whiteboardReconnectPending", "false");
      sessionStorage.setItem("codingReconnectPending", "false");
    }
  };

  // Fetch panel details (only after network is stable + servers warmed up)
  useEffect(() => {
    const fetchPanelDetails = async () => {
      if (!allowProceedTop || warmingUp || !apiAlive) return;

      try {
        const companyId = sessionStorage.getItem("companyId") || "";
        const interviewId = sessionStorage.getItem("interviewId");
        const url = `${process.env.REACT_APP_API_OCR_BASE_URL}/get-panel-details/?interview_id=${interviewId}&company_id=${companyId}`;
        const response = await authenticatedFetch(url, { method: "GET" });

        const data = await response.json();

        if (!isPanelDetailsValid(data)) {
          throw new Error("Invalid panel data");
        }

        setPanelDetails(data);
        sessionStorage.setItem("panelDetails", JSON.stringify(data));

        // Run right after panel details succeed, same gating as above.
        await runPreReconnectionCheck();
      } catch (error) {
        console.error("Error fetching panel details:", error);
        toast.error("Failed to load panel details");
      } finally {
        setIsLoading(false);
      }
    };

    // if API becomes alive later, this will run automatically
    fetchPanelDetails();
  }, [allowProceedTop, warmingUp, apiAlive]);
  // Cleanup media stream on unmount
  useEffect(() => {
    return () => {
      if (mediaStream) mediaStream.getTracks().forEach((track) => track.stop());
    };
  }, [mediaStream]);

  // ---- Environment Check: Camera -> Microphone -> Speakers ----
  const [checkStatus, setCheckStatus] = useState({
    camera: "pending", // pending | testing | success | fail
    mic: "pending",
    speaker: "pending",
  });
  const [activeCheck, setActiveCheck] = useState(null); // "camera" | "mic" | "speaker" | null
  const checkVideoRef = useRef(null);
  const micCheckRef = useRef({
    stream: null,
    audioCtx: null,
    interval: null,
    startedAt: null,
    baselineSamples: [],
    baseline: null,
    voiceFrameCount: 0,
  });

  const allChecksPassed =
    checkStatus.camera === "success" &&
    checkStatus.mic === "success" &&
    checkStatus.speaker === "success";

  const stopMicCheckResources = () => {
    const ref = micCheckRef.current;
    if (ref.interval) clearInterval(ref.interval);
    if (ref.stream) ref.stream.getTracks().forEach((track) => track.stop());
    if (ref.audioCtx) {
      try {
        ref.audioCtx.close();
      } catch {
        // already closed
      }
    }
    micCheckRef.current = {
      stream: null,
      audioCtx: null,
      interval: null,
      startedAt: null,
      baselineSamples: [],
      baseline: null,
      voiceFrameCount: 0,
    };
  };

  const startCameraCheck = async () => {
    setCheckStatus((s) => ({ ...s, camera: "testing" }));
    setActiveCheck("camera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      setMediaStream(stream);
      if (checkVideoRef.current) {
        checkVideoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Camera check failed:", error);
      setCheckStatus((s) => ({ ...s, camera: "fail" }));
      setActiveCheck(null);
      toast.error("Unable to access your camera. Please check permissions.", {
        style: {
          background: "#dc2626",
          color: "#ffffff",
          fontWeight: "600",
        },
      });
    }
  };

  const confirmCameraCheck = (canSeeSelf) => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
    }
    setActiveCheck(null);

    if (canSeeSelf) {
      setCheckStatus((s) => ({ ...s, camera: "success" }));
    } else {
      setCheckStatus((s) => ({ ...s, camera: "fail" }));
      toast.error("Please check your camera connection and try again.", {
        style: {
          background: "#dc2626",
          color: "#ffffff",
          fontWeight: "600",
        },
      });
    }
  };

  // Human voice fundamentals + harmonics live roughly in 85Hz-4kHz.
  // Restricting the analysis to that band (instead of averaging across
  // the full spectrum, most of which is empty for speech) gives a much
  // more reliable signal-to-noise read.
  const VOICE_FREQ_MIN_HZ = 85;
  const VOICE_FREQ_MAX_HZ = 4000;
  // A fixed absolute threshold doesn't work across devices: some mics/rooms
  // have a much higher ambient noise floor than others. Instead we measure
  // each device's own ambient floor for a short window right after the
  // stream opens, then require speech to clearly exceed *that* device's
  // floor, and to do so for a sustained stretch (not a single noise blip).
  const MIC_BASELINE_WINDOW_MS = 600; // time to measure ambient noise before listening for speech
  const MIC_VOICE_MARGIN = 18; // how far above this device's ambient floor counts as speech
  const MIC_MIN_VOICE_FRAMES = 4; // require ~400ms of cumulative speech-level energy, not a single spike
  const MIC_MIN_SAMPLE_MS = 2200; // overall minimum test duration (covers calibration + speaking)

  const startMicCheck = async () => {
    setCheckStatus((s) => ({ ...s, mic: "testing" }));
    setActiveCheck("mic");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      setMediaStream(stream);

      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const binHz = audioContext.sampleRate / analyser.fftSize;
      const minBin = Math.max(0, Math.floor(VOICE_FREQ_MIN_HZ / binHz));
      const maxBin = Math.min(
        dataArray.length - 1,
        Math.ceil(VOICE_FREQ_MAX_HZ / binHz),
      );

      micCheckRef.current.stream = stream;
      micCheckRef.current.audioCtx = audioContext;
      micCheckRef.current.startedAt = Date.now();
      micCheckRef.current.baselineSamples = [];
      micCheckRef.current.baseline = null;
      micCheckRef.current.voiceFrameCount = 0;

      micCheckRef.current.interval = setInterval(() => {
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = minBin; i <= maxBin; i++) sum += dataArray[i];
        const voiceBandAvg = sum / (maxBin - minBin + 1);

        const elapsed = Date.now() - micCheckRef.current.startedAt;

        // Still in the calibration window: just record the ambient floor.
        if (elapsed < MIC_BASELINE_WINDOW_MS) {
          micCheckRef.current.baselineSamples.push(voiceBandAvg);
          return;
        }

        // Calibration window just ended: lock in the baseline once.
        if (micCheckRef.current.baseline === null) {
          const samples = micCheckRef.current.baselineSamples;
          micCheckRef.current.baseline =
            samples.length > 0
              ? samples.reduce((a, b) => a + b, 0) / samples.length
              : 0;
        }

        if (voiceBandAvg > micCheckRef.current.baseline + MIC_VOICE_MARGIN) {
          micCheckRef.current.voiceFrameCount += 1;
        }
      }, 100);
    } catch (error) {
      console.error("Microphone check failed:", error);
      setCheckStatus((s) => ({ ...s, mic: "fail" }));
      setActiveCheck(null);
      toast.error(
        "Unable to access your microphone. Please check permissions.",
        {
          style: {
            background: "#dc2626",
            color: "#ffffff",
            fontWeight: "600",
          },
        },
      );
    }
  };

  const confirmMicCheck = () => {
    const { voiceFrameCount, startedAt } = micCheckRef.current;
    const elapsedMs = startedAt ? Date.now() - startedAt : 0;

    if (elapsedMs < MIC_MIN_SAMPLE_MS) {
      toast.warn("Please speak a little louder for a couple of seconds.", {
        style: {
          background: "#f97316",
          color: "#ffffff",
          fontWeight: "600",
        },
      });
      return;
    }

    stopMicCheckResources();
    setMediaStream(null);
    setActiveCheck(null);

    if (voiceFrameCount >= MIC_MIN_VOICE_FRAMES) {
      setCheckStatus((s) => ({ ...s, mic: "success" }));
    } else {
      setCheckStatus((s) => ({ ...s, mic: "fail" }));
      toast.warn(
        "We couldn't hear you clearly. Please check your microphone and try again.",
        {
          style: {
            background: "#f97316",
            color: "#ffffff",
            fontWeight: "600",
          },
        },
      );
    }
  };

  // Closing the dialog (X) should cancel the test, not silently submit
  // whatever level happened to be captured so far.
  const cancelMicCheck = () => {
    stopMicCheckResources();
    setMediaStream(null);
    setActiveCheck(null);
    setCheckStatus((s) => ({ ...s, mic: "pending" }));
  };

  const startSpeakerCheck = () => {
    setCheckStatus((s) => ({ ...s, speaker: "testing" }));
    setActiveCheck("speaker");
    try {
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 440;
      gain.gain.value = 0.2;
      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start();
      setTimeout(() => {
        try {
          oscillator.stop();
          audioContext.close();
        } catch {
          // already stopped
        }
      }, 1200);
    } catch (error) {
      console.error("Speaker check failed:", error);
    }
  };

  const confirmSpeakerCheck = (canHear) => {
    setActiveCheck(null);
    if (canHear) {
      setCheckStatus((s) => ({ ...s, speaker: "success" }));
    } else {
      setCheckStatus((s) => ({ ...s, speaker: "fail" }));
      toast.error(
        "Please check your speaker/headphone connection and try again.",
        {
          style: {
            background: "#dc2626",
            color: "#ffffff",
            fontWeight: "600",
          },
        },
      );
    }
  };

  const checkOrder = ["camera", "mic", "speaker"];

  // A given check can only be (re)run once everything before it in the
  // sequence has succeeded — this stops someone from testing "speaker"
  // straight away and never actually verifying camera/mic.
  const isCheckUnlocked = (key) => {
    const idx = checkOrder.indexOf(key);
    return checkOrder
      .slice(0, idx)
      .every((priorKey) => checkStatus[priorKey] === "success");
  };

  const retryCheck = (type) => {
    if (!isCheckUnlocked(type)) {
      toast.warn("Please complete the previous check first.", {
        style: {
          background: "#f97316",
          color: "#ffffff",
          fontWeight: "600",
        },
      });
      return;
    }
    if (type === "camera") startCameraCheck();
    if (type === "mic") startMicCheck();
    if (type === "speaker") startSpeakerCheck();
  };

  // (allChecksPassed is read directly wherever gating is needed; no separate
  // mirrored state is kept, since the old micTestResult mirror was unused.)

  // Clean up mic-check resources on unmount
  useEffect(() => {
    return () => stopMicCheckResources();
  }, []);

  const checkRowConfig = [
    {
      key: "camera",
      label: "Video Camera",
      iconUnchecked: videoUnchecked,
      iconChecked: videochecked,
    },
    {
      key: "mic",
      label: "Microphone (Input)",
      iconUnchecked: microphoneUnchecked,
      iconChecked: microphoneChecked,
    },
    {
      key: "speaker",
      label: "Speakers (Output)",
      iconUnchecked: speakerUnchecked,
      iconChecked: speakerChecked,
    },
  ];

  const renderCheckRow = ({ key, label, iconUnchecked, iconChecked }) => {
    const status = checkStatus[key];
    const isSuccess = status === "success";
    const isTesting = status === "testing";
    const isFail = status === "fail";
    const unlocked = isCheckUnlocked(key);
    const icon = isSuccess ? iconChecked : iconUnchecked;

    return (
      <div
        key={key}
        className={`flex items-center justify-between mb-4 w-full ${
          unlocked ? "" : "opacity-50"
        }`}
      >
        <div className="flex items-center">
          <img
            src={icon}
            alt={label}
            onClick={() => {
              // Clicking the icon does the same thing as the "Tap to test" /
              // "Retry test" / "Re-test" button. retryCheck() already
              // handles the "previous step not done yet" toast, so we just
              // need to avoid restarting a check that's actively running.
              if (!isTesting) retryCheck(key);
            }}
            className={`w-9 h-9 shrink-0 ${isTesting ? "" : "cursor-pointer"}`}
          />
          <p className="ml-3 font-normal text-sm text-fontColor flex items-center whitespace-nowrap">
            {label}
            {isSuccess && (
              <img
                src={greenTickMark}
                alt="verified"
                className="w-4 h-4 ml-2"
              />
            )}
            {isFail && (
              <span className="ml-2 text-red-500 text-xs">Failed</span>
            )}
          </p>
        </div>

        {!unlocked && (
          <span className="text-gray-400 text-xs whitespace-nowrap">
            Complete previous step
          </span>
        )}

        {unlocked && isTesting && (
          <span className="text-gray-400 text-xs whitespace-nowrap">
            Testing…
          </span>
        )}

        {unlocked && !isTesting && (status === "pending" || isFail) && (
          <button
            type="button"
            onClick={() => retryCheck(key)}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-[#111729] hover:bg-[#1a2035] whitespace-nowrap"
          >
            {isFail ? "Retry test" : "Tap to test"}
          </button>
        )}

        {unlocked && isSuccess && (
          <button
            type="button"
            onClick={() => retryCheck(key)}
            className="text-xs font-medium text-gray-400 hover:text-gray-600 whitespace-nowrap"
          >
            Re-test
          </button>
        )}
      </div>
    );
  };

  const EnvironmentCheckModal = () => {
    if (!activeCheck) return null;

    if (activeCheck === "camera") {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-md shadow-xl w-[460px] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-[#E7E9EB] border-b border-[#CCCCCC]">
              <p className="font-medium text-sm">Confirming...</p>
              <button
                onClick={() => confirmCameraCheck(false)}
                className="text-gray-500"
              >
                ✕
              </button>
            </div>
            <div className="p-6 flex flex-col items-center text-[#111729]">
              <p className="font-semibold mb-6">Testing Video Camera…</p>
              <p className="text-sm mb-8 text-center text-[#111729]">
                Are you able to see yourself in the video on the left?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => confirmCameraCheck(false)}
                  className="px-8 py-2 border rounded-md flex items-center space-x-1 text-sm"
                >
                  <img src={noIcon} alt="No" className="w-4 h-4 mr-2" />
                  <span>No</span>
                </button>
                <button
                  onClick={() => confirmCameraCheck(true)}
                  className="px-8 py-2 bg-[#002147] text-white rounded-md flex items-center space-x-1 text-sm"
                >
                  <img src={yesIcon} alt="Yes" className="w-4 h-4 mr-2" />
                  <span>Yes</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeCheck === "mic") {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-md shadow-xl w-[420px] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
              <p className="font-medium text-sm">Testing Microphone</p>
              <button onClick={cancelMicCheck} className="text-gray-500">
                ✕
              </button>
            </div>
            <div className="p-6 flex flex-col items-center text-[#111729]">
              <p className="font-semibold mb-4">
                Speak a few words to test your microphone.
              </p>
              {/* <p className="text-gray-700 mb-2">Big Brown Fox</p> */}
              <span className="text-amber-500 text-2xl mb-4">
                {" "}
                <img
                  src={audioWave}
                  alt="Audio Wave"
                  className="w-8 h-6 mr-2"
                />
              </span>
              <button
                onClick={confirmMicCheck}
                className="px-8 py-2 bg-[#111729] text-white rounded-md flex items-center space-x-1 text-sm"
              >
                <img src={yesIcon} alt="Yes" className="w-4 h-4 mr-2" />
                <span>OK</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (activeCheck === "speaker") {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-md shadow-xl w-[420px] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
              <p className="font-medium text-sm">Testing Speakers</p>
              <button
                onClick={() => confirmSpeakerCheck(false)}
                className="text-gray-500"
              >
                ✕
              </button>
            </div>
            <div className="p-6 flex flex-col items-center">
              <p className="font-semibold mb-8 mt-4 text-[#111729]">
                Were you able to hear the audio?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => confirmSpeakerCheck(false)}
                  className="px-8 py-2 border rounded-md flex items-center space-x-1 text-sm"
                >
                  <img src={noIcon} alt="No" className="w-4 h-4" />
                  <span>No</span>
                </button>
                <button
                  onClick={() => confirmSpeakerCheck(true)}
                  className="px-8 py-2 bg-[#111729] text-white rounded-md flex items-center space-x-1 text-sm"
                >
                  <img src={yesIcon} alt="Yes" className="w-4 h-4" />
                  <span>Yes</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const EnvironmentCheckSection = () => (
    <div
      className="flex flex-col items-center text-center w-full"
      style={{ marginTop: showBatteryWarning ? "-60px" : undefined }}
    >
      <div className="inline-block">
        <p className="text-xl sm:text-[28px] font-semibold mb-6 text-center">
          {allChecksPassed
            ? "Your environment is all set for the assessment."
            : "Let's do a quick check if your system environment is ready for the assessment…"}
        </p>
        <div className="mb-6 flex flex-col items-stretch ml-10">
          {checkRowConfig.map(renderCheckRow)}
        </div>
      </div>
      <EnvironmentCheckModal />
    </div>
  );

  // Meeting join handler
  const handleJoinMeeting = async () => {
    if (!isPanelReady) {
      toast.warn("Panel details are still loading. Please wait...");
      return;
    }
    if (!allowProceedTop) {
      toast.warn("Please switch to a stable internet connection to continue.");
      return;
    }

    if (warmingUp || !apiAlive) {
      toast.info("Preparing assessment environment. Please wait...");
      return;
    }

    batteryUnsubscribeRef.current?.();
    batteryUnsubscribeRef.current = null;

    // const result = await checkInternetQuality();
    // setNetStatus(result);

    // if (!result.ok) {
    //   toast.warn(result.message || "Internet too slow. Please try again.");
    //   return;
    // }
    setIsCreatingMeeting(true);
    try {
      if (_handleOnCreateMeeting) {
        const { meetingId: createdMeetingId } = await _handleOnCreateMeeting();
        const id = createdMeetingId || generateMeetingId();
        setMeetingId(id);
        onClickJoin(id, panelDetails);
      } else {
        const generatedMeetingId = generateMeetingId();
        setMeetingId(generatedMeetingId);
        onClickJoin(generatedMeetingId, panelDetails);
      }
    } catch (error) {
      console.error("Error creating meeting:", error);
      toast.error("Failed to create meeting. Please try again.");
    } finally {
      setIsCreatingMeeting(false);
    }
  };

  if (isLoading && !warmingUp && netStatus === "checking")
    return <p className="text-center mt-10">Loading...</p>;

  // If network is not stable, show only connection warning (no panel UI)
  if (!allowProceedTop && !warmingUp) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <BatteryWarning />
        <div
          className={`w-full max-w-[520px] rounded-xl px-4 py-4 text-sm font-medium ${
            netStatus === "offline"
              ? "bg-red-50 text-red-700 border border-red-200"
              : netStatus === "unstable"
                ? "bg-orange-50 text-orange-700 border border-orange-200"
                : "bg-yellow-50 text-yellow-700 border border-yellow-200"
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="text-2xl leading-none">
              {netStatus === "offline" ? "❌" : "⚠️"}
            </div>

            <div className="flex-1">
              <p className="text-base font-extrabold tracking-wide">
                {msg.title}
              </p>
              <p className="text-sm mt-1 font-semibold opacity-95">
                {msg.desc}
              </p>
              <p className="text-xs mt-2 font-medium opacity-80">
                Please use a stable internet connection to continue the
                assessment.
              </p>

              <p className="text-xs mt-2 font-semibold opacity-80">
                {latencyMs !== null && <span>Latency: {latencyMs}ms</span>}
                {lossPct !== null && (
                  <span className="ml-2">Loss: {lossPct}%</span>
                )}
              </p>

              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-5 py-2 rounded-xl bg-btnBgColor text-white"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const panelType = panelDetails?.panel?.toLowerCase() || "pnlo";
  const isMultiPanel = panelType === "pnla" || panelType === "pnlb";

  // Don't reveal panel members until the camera/mic/speaker checks pass.
  // The checks only run on the "Start Assessment" flow, so on a restored
  // ("Continue Assessment") flow there's nothing to gate on.
  const canShowPanel = !isStartAssessment || allChecksPassed;

  //  MULTI PANEL UI (PNLA / PNLB)

  //  MULTI PANEL UI (PNLA / PNLB)
  if (isMultiPanel) {
    const participants = Object.entries(panelDetails)
      .filter(([key]) => key.startsWith("pnl") && key !== "panel")
      .map(([key, value]) => ({
        key,
        name: toTitleCase(value),
      }))
      .filter(({ name }) => Boolean(name));

    const formattedNames =
      participants.length > 1
        ? `${participants
            .slice(0, -1)
            .map((p) => p.name)
            .join(", ")} and ${participants.slice(-1)[0].name}`
        : participants[0]?.name;

    // Assign profile images based on panel type
    let assignedImages = [];

    if (panelType === "pnla") {
      // 2 male, 1 female
      assignedImages = [profileImgMale3, profileImgMale2, profileImg];
    } else if (panelType === "pnlb") {
      // 2 female, 1 male
      assignedImages = [profileImgMale3, profileImg, profileImgFemale1];
    } else {
      // fallback — all default
      assignedImages = [profileImg, profileImgFemale1, profileImgMale2];
    }

    return (
      <>
        <BatteryWarning />
        {/* Warmup overlay (same behavior as login) */}
        {warmingUp && (
          <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center px-4">
            {!warmupFailed ? (
              <>
                <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-logoColor mb-5"></div>

                <p className="text-fontColor text-lg font-semibold text-center">
                  {netStatus === "offline"
                    ? "No internet connection"
                    : netStatus === "unstable"
                      ? "Network is unstable"
                      : prepSecondsLeft !== null
                        ? "Preparing your assessment environment"
                        : "Getting your assessment ready"}
                </p>

                <p className="text-fontColor text-sm mt-2 opacity-70 text-center max-w-[360px]">
                  {netStatus === "offline"
                    ? "Please reconnect to Wi-Fi/Mobile data to continue."
                    : netStatus === "unstable"
                      ? msg.desc
                      : prepSecondsLeft !== null
                        ? "This may take up to the time shown below. Please don’t refresh the page."
                        : "Connecting securely and starting required services..."}
                </p>

                {prepSecondsLeft !== null && (
                  <div className="mt-4 text-fontColor text-base font-medium">
                    Should be ready in{" "}
                    <span className="font-bold">
                      {String(Math.floor(prepSecondsLeft / 60)).padStart(
                        2,
                        "0",
                      )}
                      :{String(prepSecondsLeft % 60).padStart(2, "0")}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-fontColor text-lg font-semibold text-center">
                  We’re facing high traffic right now
                </p>

                <p className="text-fontColor text-sm mt-2 opacity-70 text-center max-w-[360px]">
                  The assessment services are taking longer than expected.
                  <br />
                  Please try again in a few minutes.
                </p>

                <button
                  onClick={() => window.location.reload()}
                  className="mt-6 px-6 py-2 rounded-xl bg-btnBgColor text-white"
                >
                  Retry
                </button>
              </>
            )}
          </div>
        )}
        <div className="flex flex-col items-center justify-center w-full mt-10">
          {/*  Environment Check (Camera / Mic / Speaker) */}
          {isStartAssessment && <EnvironmentCheckSection />}
          {isHeadphoneRequired &&
            !isHeadphoneConnected &&
            !headphoneNeedsManualConfirmation && <HeadphoneWarningMessage />}
          {isHeadphoneRequired &&
            !isHeadphoneConnected &&
            headphoneNeedsManualConfirmation && (
              <HeadphoneManualConfirmMessage />
            )}
          {isDesktopAgentRequired && !isDesktopAgentRunning && (
            <DesktopAgentWarningMessage />
          )}
          {hasExternalMonitor && <MonitorWarningMessage />}

          {/*  Panel Members */}
          {canShowPanel && (
            <>
              <p className="text-primaryFontSize font-medium mb-5">
                {formattedNames
                  ? `${formattedNames} ${
                      participants.length > 1 ? "are" : "is"
                    } on the call.`
                  : "No panel members found."}
              </p>

              <div className="flex space-x-6 mb-10 flex-wrap justify-center">
                {participants.map((participant, index) => {
                  const imgSrc = assignedImages[index % assignedImages.length];
                  return (
                    <div key={index} className="flex flex-col items-center">
                      <img
                        src={imgSrc}
                        alt={participant.name}
                        className="h-16 w-16 rounded-full"
                      />
                      {/* <p className="text-primaryFontSize font-medium mt-2">{participant.name}</p> */}
                    </div>
                  );
                })}
              </div>
              <p className="font-medium text-primaryFontSize mb-5">
                Join when you are ready!
              </p>
            </>
          )}

          {/* Join Button */}

          {!isCameraPermissionGranted && <CameraWarningMessage />}
          {!isWindowMgmtPermissionGranted && <WindowManagementWarningMessage />}
          {!isPanelReady && (
            <p className="text-sm text-gray-500 mb-3">
              Loading panel details...
            </p>
          )}
          {!allowProceedTop && (
            <div className="mt-4 mb-2 px-4 py-2 rounded-md bg-yellow-100 text-yellow-800 text-sm font-medium">
              {msg.title} — {msg.desc}
              {latencyMs !== null && (
                <span className="ml-2">Latency: {latencyMs}ms</span>
              )}
              {lossPct !== null && (
                <span className="ml-2">Loss: {lossPct}%</span>
              )}
            </div>
          )}

          <div className="mt-6 flex w-full justify-center">
            <button
              className={`bg-[#111729] text-white px-6 py-2 rounded-md text-btnFontSize font-medium flex items-center space-x-2 transition-all ${
                isCreatingMeeting ||
                hasExternalMonitor ||
                warmingUp ||
                !apiAlive ||
                !isCameraPermissionGranted ||
                !isWindowMgmtPermissionGranted ||
                (isDesktopAgentRequired && !isDesktopAgentRunning) ||
                !allowProceedTop ||
                (isHeadphoneRequired && !isHeadphoneConnected) ||
                (isStartAssessment && !allChecksPassed)
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-[#1a2035]"
              }`}
              onClick={handleJoinMeeting}
              disabled={
                hasExternalMonitor ||
                isCreatingMeeting ||
                warmingUp ||
                !apiAlive ||
                !isCameraPermissionGranted ||
                !isWindowMgmtPermissionGranted ||
                !isPanelReady ||
                (isDesktopAgentRequired && !isDesktopAgentRunning) ||
                !allowProceedTop ||
                (isHeadphoneRequired && !isHeadphoneConnected) ||
                (isStartAssessment && !allChecksPassed)
              }
            >
              <img src={webCam} alt="webcam" className="w-5 h-5" />
              <span>
                {isCreatingMeeting ? "Creating Meeting..." : buttonText}
              </span>
            </button>
          </div>
        </div>
      </>
    );
  }

  // SINGLE PANEL UI (PNLO)
  return (
    <div className="flex flex-1 flex-col justify-center items-center w-full">
      <BatteryWarning />
      {/*  Environment Check (Camera / Mic / Speaker) */}
      {isStartAssessment && <EnvironmentCheckSection />}
      {isHeadphoneRequired &&
        !isHeadphoneConnected &&
        !headphoneNeedsManualConfirmation && <HeadphoneWarningMessage />}
      {isHeadphoneRequired &&
        !isHeadphoneConnected &&
        headphoneNeedsManualConfirmation && <HeadphoneManualConfirmMessage />}
      {isDesktopAgentRequired && !isDesktopAgentRunning && (
        <DesktopAgentWarningMessage />
      )}
      {!isCameraPermissionGranted && <CameraWarningMessage />}
      {!isWindowMgmtPermissionGranted && <WindowManagementWarningMessage />}
      {hasExternalMonitor && <MonitorWarningMessage />}

      {/* Single user info */}
      <div className="flex flex-col justify-center items-center">
        <p className="text-heroFontSize font-medium mt-20">
          Join the assessment when you are ready!
        </p>
        {isPanelReady && canShowPanel && (
          <img src={profileImg} alt="profile" className="h-16 w-16 mt-16" />
        )}

        {(() => {
          const panelDetails = JSON.parse(
            sessionStorage.getItem("panelDetails") || "{}",
          );
          const { panel, pnlf1, pnlf2, pnlm1, pnlm2 } = panelDetails || {};

          // Default to "Anushka" if no valid name found
          let displayName = "Anushka";

          if (panel === "PNLO") {
            // For single-user case, use pnlf1 first, then fallback
            displayName = pnlf1 || pnlf2 || pnlm1 || pnlm2 || "Anushka";
          } else {
            // For multi-panel case, use panel name directly (PNLA, PNL B, etc.)
            displayName = panel?.toLowerCase() || "Anushka";
          }

          return (
            isPanelReady &&
            canShowPanel && (
              <p className="font-medium mt-2 text-primaryFontSize">
                {displayName.charAt(0).toUpperCase() + displayName.slice(1)} is
                ready to join the call.
              </p>
            )
          );
        })()}
      </div>
      {/* {!allowProceedTop && (
        <div className="mb-3 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-yellow-900">
          <div className="font-semibold">{msg.title}</div>
          <div className="text-sm">
            {msg.desc}
            {latencyMs !== null && (
              <span className="ml-2">Latency: {latencyMs}ms</span>
            )}
            {lossPct !== null && <span className="ml-2">Loss: {lossPct}%</span>}
          </div>
        </div>
      )} */}
      {/*  Join Button */}
      <div className="flex flex-1 flex-col mt-5 w-2/3">
        <div className="w-full flex flex-col items-center">
          {!isPanelReady && (
            <p className="text-sm text-gray-500 mb-3">
              Loading panel details...
            </p>
          )}
          {!allowProceedTop && (
            <div className="mt-4 mb-3 w-full max-w-xl animate-pulse">
              <div className="flex items-start gap-3 px-5 py-4 rounded-lg border border-yellow-300 bg-yellow-50 text-yellow-900 shadow-sm">
                <div className="mt-0.5">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-yellow-200">
                    ⚠️
                  </span>
                </div>

                <div className="flex-1">
                  <p className="text-base font-semibold leading-snug">
                    {msg.title}
                  </p>
                  <p className="text-sm font-medium mt-1">{msg.desc}</p>

                  {(latencyMs !== null || lossPct !== null) && (
                    <p className="text-xs mt-2 font-semibold">
                      {latencyMs !== null && (
                        <span>Latency: {latencyMs} ms</span>
                      )}
                      {lossPct !== null && (
                        <span className="ml-2">Loss: {lossPct}%</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex w-full justify-center">
            <button
              className={`bg-[#111729] text-white px-6 py-2 rounded-md text-btnFontSize font-medium flex items-center space-x-2 transition-all ${
                isCreatingMeeting ||
                hasExternalMonitor ||
                warmingUp ||
                !apiAlive ||
                !isCameraPermissionGranted ||
                !isWindowMgmtPermissionGranted ||
                !allowProceedTop ||
                (isDesktopAgentRequired && !isDesktopAgentRunning) ||
                (isHeadphoneRequired && !isHeadphoneConnected) ||
                (isStartAssessment && !allChecksPassed)
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-[#1a2035]"
              }`}
              onClick={handleJoinMeeting}
              disabled={
                isCreatingMeeting ||
                hasExternalMonitor ||
                warmingUp ||
                !apiAlive ||
                !isCameraPermissionGranted ||
                !isWindowMgmtPermissionGranted ||
                !allowProceedTop ||
                (isDesktopAgentRequired && !isDesktopAgentRunning) ||
                !isPanelReady ||
                (isHeadphoneRequired && !isHeadphoneConnected) ||
                (isStartAssessment && !allChecksPassed)
              }
            >
              <img src={webCam} alt="webcam" className="w-5 h-5" />
              <span>
                {isCreatingMeeting ? "Creating Meeting..." : buttonText}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
